/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode } from 'react';

/**
 * The error page's visual body — status number, heading, messages, and the "back home" CTA. Purely
 * presentational: every string arrives as a prop, so it renders identically whether the caller
 * resolved its copy through i18n or is falling back to hardcoded English.
 *
 * Shared by the two error surfaces so they can't drift apart (UCR-3048):
 * - {@link RouteErrorContent} — storefront errors, wrapped in the full chrome by `_app`'s
 *   `ErrorBoundary`. This is the branded 404 shoppers see.
 * - `root.tsx`'s `ErrorPageContent` — the standalone fallback for errors thrown before/outside the
 *   `_app` layout (e.g. a middleware failure), which supplies its own minimal header and footer.
 *
 * NOTE: the width cap is an explicit rem value on purpose. This project's design-system theme remaps
 * Tailwind's `max-w-2xl`/`max-w-3xl` onto the *spacing* scale (~tens of px), so using them here
 * collapses the text to an unreadable column — which is exactly what the standalone page did before
 * it started sharing this component. The caller owns the outer page width (`section-container`).
 */
export function ErrorBody({
    status,
    title,
    message,
    secondaryMessage,
    statusDetails,
    detailsText,
    ctaHref,
    ctaLabel,
    children,
}: {
    /** HTTP status, when the error is a route error response. Rendered as the large numeral. */
    status?: number;
    title: string;
    message?: string;
    secondaryMessage?: string;
    statusDetails?: string;
    /** Technical detail for non-route errors (no status), rendered as `Error: {detailsText}`. */
    detailsText?: string;
    ctaHref: string;
    ctaLabel: string;
    /** Extra content below the CTA — the standalone page uses it for its dev-only stack trace. */
    children?: ReactNode;
}): ReactElement {
    return (
        <div className="flex min-h-[50vh] items-center justify-center py-12">
            <div className="mx-auto w-full max-w-[42rem] text-center">
                {status && <div className="text-error-status font-bold leading-none mb-8">{status}</div>}
                <h1 className="text-4xl md:text-5xl font-bold mb-6">{title}</h1>
                {message && <p className="text-lg text-muted-foreground mb-4">{message}</p>}
                {secondaryMessage && <p className="text-base text-muted-foreground mb-4">{secondaryMessage}</p>}
                {statusDetails && <p className="text-sm text-muted-foreground mb-12 opacity-50">{statusDetails}</p>}
                {detailsText && <p className="text-lg text-muted-foreground mb-12">Error: {detailsText}</p>}
                <div>
                    <a
                        href={ctaHref}
                        className="inline-block rounded-none bg-primary px-12 py-3 text-base font-semibold text-primary-foreground no-underline transition-colors hover:bg-primary/90">
                        {ctaLabel}
                    </a>
                </div>
                {children}
            </div>
        </div>
    );
}
